<template>
    <div class="tabs" ref="ancillaryTabs">
        <tabs v-model:value="activeDetailsTab" v-if="detailsTabs" @update:value="activeDetailsTabUpdated">
            <tab-list>
                <tab :value="tab.id" v-for="tab in detailsTabs" :key="tab.id">
                    {{ tab.name }}
                </tab>
            </tab-list>
        </tabs>
        <tabs v-model:value="selectedCategory" class="sub-tabs" v-if="selectedCategory && activeDetailsTab === 'charts'" @update:value="getCharts" scrollable>
            <tab-list>
                <tab :value="tab.id" v-for="tab in categoriesToDisplay" :key="tab.id">
                    {{ tab.name }}
                </tab>
            </tab-list>
        </tabs>
        <tabs v-else-if="activeDetailsTab !== 'remit'" class="sub-tabs scroll-tab" v-model:value="activeSubDetailsTab" scrollable show-navigators @update:value="activeSubDetailsTabUpdated">
            <tab-list>
                <tab :value="tab.name" v-for="tab in subDetailsTabs" :key="tab.id">
                    {{ tab.name }}
                </tab>
            </tab-list>
        </tabs>
    </div>
</template>

<script setup lang="ts">
import {
    ref, computed, nextTick,
} from 'vue';
import type { IIdName } from '@/interfaces/general/IIdName.ts';
import { IChartsToGet } from '@/interfaces/powerMarkets/plant/IPlantCategory.ts';
import { IPlantDetailsPlusRelated } from '@/interfaces/powerMarkets/plant/IPlantDetails.ts';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import { PLANT_DETAILS_TABS, type PlantDetailsTab } from '@/constants/powerMarkets/plantDetailsTabs.ts';

const detailsTabs = PLANT_DETAILS_TABS;

const dataStore = useDataStore();

const props = defineProps<{
    plantInfo: Record<string, IPlantDetailsPlusRelated>;
    initialDetailsTab?: PlantDetailsTab;
}>();

const emit = defineEmits<{
    'update:chart-category': [string[]];
    'update:active-details-tab': [PlantDetailsTab];
    'update:active-sub-details-tab': [string];
}>();

const activeDetailsTab = ref<PlantDetailsTab | ''>(props.initialDetailsTab ?? '');
const activeSubDetailsTab = ref<string>('');
const categoriesWithCharts = ref<IChartsToGet | null>(null);
const selectedCategory = ref<string | null>(null);

const ancillaryTabs = ref<HTMLElement | null>(null);

const plantIds = computed<string[]>(() => Object.keys(props.plantInfo));

const categoriesToDisplay = computed<Array<IIdName>>(() => {
    if (categoriesWithCharts.value) {
        const categories = Object.keys(categoriesWithCharts.value);
        return categories.map((key) => ({ id: key, name: key }));
    }
    return [];
});

const subDetailsTabs = computed<Array<IIdName>>(() => {
    if (!props.plantInfo) {
        return [];
    }

    if (activeDetailsTab.value === 'charts') {
        return categoriesToDisplay.value;
    }

    if (activeDetailsTab.value === 'extraInfo') {
        const mapped = Object.values(props.plantInfo).flatMap(plant => plant.plantInfo!.extraData.map(el => ({
            id: el.name,
            name: el.name,
        })));

        const unique = Array.from(
            new Map(mapped.map(item => [item.id, item])).values(),
        );

        return [
            { id: 'Dynamic data', name: 'Dynamic data' },
            ...unique,
        ];
    }

    return Object.values(props.plantInfo).flatMap(plant => plant.plantInfo!.tableData.map((el) => ({ id: el.tabName, name: el.tabName })));
});

function getCharts (category: string | number): void {
    if (!categoriesWithCharts.value) return;
    emit('update:chart-category', categoriesWithCharts.value[category as keyof IChartsToGet]);
}

function getCategoriesWithCharts (): void {
    if (!dataStore.plantStore?.allPlants) return;

    const categoriesToReturn = {} as IChartsToGet;
    plantIds.value.forEach(plantId => {
        const categories = Object.values(dataStore.plantStore!.allPlants).find((holder) => holder.plants.some((plant: { id: string }) => plant.id === plantId));
        const fuel = categories?.plants.find((plant: { id: string }) => plant.id === plantId)?.fuel;

        if (!categories) return;

        const chartsToAdd = fuel && categories.chartsToGetBmByFuel && categories.chartsToGetBmByFuel[fuel]
            ? categories.chartsToGetBmByFuel[fuel]
            : categories?.chartsToGetNonBm;

        Object.entries(chartsToAdd).forEach(([key, value]) => {
            if (key in categoriesToReturn && Array.isArray(categoriesToReturn[key as keyof IChartsToGet])) {
                (categoriesToReturn[key as keyof IChartsToGet] as string[]) = [...new Set([...(categoriesToReturn[key as keyof IChartsToGet] as string[]), ...value])];
            } else {
                categoriesToReturn[key as keyof IChartsToGet] = value;
            }
        });
    });
    categoriesWithCharts.value = categoriesToReturn;
}

function activeDetailsTabUpdated (): void {
    activeSubDetailsTab.value = subDetailsTabs.value.length ? subDetailsTabs.value[0].id : '';
    if (!activeDetailsTab.value) return;
    emit('update:active-details-tab', activeDetailsTab.value);
    activeSubDetailsTabUpdated();
}

function activeSubDetailsTabUpdated (): void {
    emit('update:active-sub-details-tab', activeSubDetailsTab.value);
}

function setTopOfAncillaryTabs (): void {
    const topOfAncillaryTabs = document.querySelector('div.p-tablist-tab-list') as HTMLElement;
    if (!topOfAncillaryTabs || !ancillaryTabs.value) return;

    ancillaryTabs.value.style.top = `${topOfAncillaryTabs.offsetHeight}px`;
}

async function initialise (): Promise<void> {
    if (activeDetailsTab.value === '' || !detailsTabs.some((item) => item.id === activeDetailsTab.value)) {
        activeDetailsTab.value = detailsTabs.length > 0 ? detailsTabs[0].id : '';
        activeSubDetailsTab.value = subDetailsTabs.value.length > 0 ? subDetailsTabs.value[0].id : '';
    } else if (activeSubDetailsTab.value === '' || !subDetailsTabs.value.some((item) => item.id === activeSubDetailsTab.value)) {
        activeSubDetailsTab.value = subDetailsTabs.value.length > 0 ? subDetailsTabs.value[0].id : '';
    }

    getCategoriesWithCharts();
    activeDetailsTabUpdated();
    if (categoriesWithCharts.value) {
        selectedCategory.value = Object.keys(categoriesWithCharts.value)[0];
        getCharts(selectedCategory.value);
    }

    nextTick(() => {
        setTopOfAncillaryTabs();
    });
}

initialise();

</script>

<style lang="scss" scoped>
.tabs {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow-x: hidden;
    position: sticky;
    background-color: var(--neutral100);
    z-index: 2;
    @include responsive('sm') {
        flex-direction: column;
    }
    .sub-tabs {
        padding-left: var(--defaultSpacing);
    }
    .scroll-tab {
        overflow-x: auto;

        &:hover {
            .arrow-icon-wrapper {
                background: var(--neutral100);
                .icon {
                    opacity: 1;
                }
            }
        }
    }
}
</style>
